import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, ArrowRight, Sparkles, FileDown } from "lucide-react";
import api from "@/lib/api";

export default function FinalDay() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/final-summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/support-plan/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Doctor-Appointment-Report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Your report is downloading");
    } catch (e) {
      toast.error("Could not generate the report. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  if (!data) return <div className="min-h-screen bg-brand flex items-center justify-center"><div className="w-10 h-10 rounded-full border-[3px] border-white/20 border-t-white animate-spin" /></div>;

  const startScore = data.starting_wellbeing;
  const currentScore = data.current_wellbeing_10 != null ? data.current_wellbeing_10 * 10 : null;

  return (
    <div className="min-h-screen bg-brand" data-testid="final-day-page">
      <div className="app-frame px-6 py-12">
        <div className="text-center animate-soft-scale">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: "var(--amber)" }}>
            <Trophy size={38} className="text-brand" />
          </div>
          <p className="text-white/60 text-xs font-semibold tracking-[0.2em] uppercase mt-6">You did it</p>
          <h1 className="font-serif text-5xl font-bold text-white mt-2">30 Days<br />Complete</h1>
          <p className="text-white/75 mt-4 max-w-sm mx-auto">
            You showed up for yourself for a whole month. That's something to be genuinely proud of.
          </p>
        </div>

        {/* stats */}
        <div className="mt-8 grid grid-cols-2 gap-3">
          <div className="rounded-2xl p-5" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
            <div className="font-serif text-3xl font-bold text-white">{data.days_completed}<span className="text-lg text-white/50">/{data.total_days}</span></div>
            <div className="text-white/60 text-xs mt-1">days completed</div>
          </div>
          <div className="rounded-2xl p-5" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
            <div className="font-serif text-3xl font-bold text-white">{data.checkin_count}</div>
            <div className="text-white/60 text-xs mt-1">check ins logged</div>
          </div>
          <div className="rounded-2xl p-5" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
            <div className="font-serif text-3xl font-bold text-white">{startScore ?? "–"}<span className="text-lg text-white/50">/100</span></div>
            <div className="text-white/60 text-xs mt-1">starting self reported wellbeing</div>
          </div>
          <div className="rounded-2xl p-5" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
            <div className="font-serif text-3xl font-bold text-white">{currentScore != null ? currentScore : "–"}<span className="text-lg text-white/50">/100</span></div>
            <div className="text-white/60 text-xs mt-1">current self reported wellbeing</div>
          </div>
        </div>
        <p className="text-white/40 text-xs mt-3 text-center">These are your own self reported values, not a medical measurement.</p>

        {startScore != null && data.current_wellbeing_10 != null && (
          <p className="text-white/85 text-sm mt-4 text-center" data-testid="final-wellbeing-line">
            Your self reported wellbeing changed from {Math.round(startScore / 10)}/10 to {data.current_wellbeing_10}/10.
          </p>
        )}

        {/* priorities */}
        {data.priorities?.length > 0 && (
          <div className="mt-8">
            <p className="text-white/60 text-xs font-semibold uppercase tracking-wide mb-3">The priorities you chose</p>
            <div className="flex flex-wrap gap-2">
              {data.priorities.map((p) => (
                <span key={p} className="bg-white/10 text-white rounded-full px-3 py-1.5 text-sm">{p}</span>
              ))}
            </div>
          </div>
        )}

        {/* reflections */}
        {data.reflections?.length > 0 && (
          <div className="mt-8">
            <p className="text-white/60 text-xs font-semibold uppercase tracking-wide mb-3">Your reflections along the way</p>
            <div className="space-y-3">
              {data.reflections.map((r) => (
                <div key={r.day} className="rounded-2xl p-4" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                  <div className="text-white/40 text-xs font-semibold">DAY {r.day} · {r.title}</div>
                  <div className="text-white/85 text-sm mt-1 whitespace-pre-wrap">{r.answer}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* compiled inputs from the journey */}
        {data.compiled && Object.values(data.compiled).some((a) => a.length > 0) && (
          <div className="mt-8" data-testid="final-compiled">
            <p className="text-white/60 text-xs font-semibold uppercase tracking-wide mb-3">Everything you chose along the way</p>
            <div className="space-y-3">
              {[
                ["food", "Food you focused on"],
                ["movement", "Movement you chose"],
                ["routine", "Routines you built"],
                ["stress", "Calm & focus tools"],
                ["symptom", "Symptom plans"],
                ["doctor", "Questions for your doctor"],
                ["observations", "Patterns you noticed"],
              ].filter(([k]) => (data.compiled[k] || []).length > 0).map(([k, label]) => (
                <div key={k} className="rounded-2xl p-4" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                  <div className="text-white/40 text-xs font-semibold uppercase tracking-wide">{label}</div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {data.compiled[k].map((g, i) => (
                      <span key={i} className="bg-white/10 text-white/85 rounded-full px-3 py-1 text-xs">Day {g.day}: {g.value}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 space-y-3">
          <button
            data-testid="final-download-pdf-btn"
            onClick={downloadPdf}
            disabled={downloading}
            className="w-full bg-white text-brand hover:bg-[var(--sage)] font-semibold py-4 rounded-full flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            <FileDown size={17} /> {downloading ? "Preparing…" : "Download my Doctor's Report (PDF)"}
          </button>
          <button
            data-testid="final-build-plan-btn"
            onClick={() => navigate("/plan")}
            className="w-full font-semibold py-4 rounded-full flex items-center justify-center gap-2 transition-colors border border-white/30 text-white hover:bg-white/10"
          >
            <Sparkles size={17} /> View & edit my Support Plan
          </button>
          <button
            data-testid="final-home-btn"
            onClick={() => navigate("/home")}
            className="w-full text-white/70 font-semibold py-3 rounded-full flex items-center justify-center gap-2"
          >
            Back to Home <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
